import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef,
  useMemo,
  type ReactNode,
} from 'react';
import { useAuth } from '@clerk/react';
import type { Bootstrap } from '@streamit/contracts';
import {
  api,
  ApiError,
  setTokenProvider,
  setProfileToken,
  json,
} from '@/lib/api';
type Session = {
  signedIn: boolean;
  loading: boolean;
  data: Bootstrap | null;
  error: ApiError | null;
  authorizationVersion: number;
  /** Stable through bootstrap refreshes; replaced when the authenticated session changes. */
  accountScope?: object;
  /** Native receiver information remains unavailable until its refresh succeeds. */
  receiverInfoPending?: boolean;
  receiverInfoEpoch?: number;
  refresh: (options?: { background?: boolean }) => Promise<void>;
  selectProfile: (id: string, pin?: string) => Promise<void>;
};
const Context = createContext<Session>({
  signedIn: false,
  loading: false,
  data: null,
  error: null,
  authorizationVersion: 0,
  refresh: async () => {},
  selectProfile: async () => {},
});
export const ReceiverSessionProvider = Context.Provider;
export const useSession = () => useContext(Context);
export function SessionProvider({ children }: { children: ReactNode }) {
  const { isSignedIn, isLoaded, getToken, userId, sessionId } = useAuth();
  const [data, setData] = useState<Bootstrap | null>(null),
    [loading, setLoading] = useState(false),
    [error, setError] = useState<ApiError | null>(null);
  // Each auth effect owns a fresh lifetime, including StrictMode's second setup.
  // Callbacks retain their auth scope so an old caller cannot borrow a new one.
  const owner = useMemo(
    () => ({ current: null as { refresh: number; selection: number } | null }),
    [isLoaded, isSignedIn, userId, sessionId, getToken],
  );
  const activeOwner = useRef(owner);
  activeOwner.current = owner;
  const [authorizationVersion, setVersion] = useState(0);
  const refresh = useCallback(async (options?: { background?: boolean }) => {
    const lifetime = owner.current;
    if (!isLoaded || !isSignedIn || !lifetime || activeOwner.current !== owner) return;
    const request = ++lifetime.refresh;
    const current = () => activeOwner.current === owner &&
      owner.current === lifetime && lifetime.refresh === request;
    if (!options?.background) setLoading(true);
    setError(null);
    try {
      const result = await api<Bootstrap>('/bootstrap');
      if (current()) setData(result);
    } catch (e) {
      if (current())
        setError(
          e instanceof ApiError
            ? e
            : new ApiError('Could not load your account.', 0, 'NETWORK_ERROR'),
        );
    } finally {
      if (current()) setLoading(false);
    }
  }, [isLoaded, isSignedIn, owner]);
  useEffect(() => {
    owner.current = { refresh: 0, selection: 0 };
    setLoading(false);
    setData(null);
    setError(null);
    setProfileToken();
    setTokenProvider(getToken);
    if (isLoaded && isSignedIn) void refresh();
    return () => {
      owner.current = null;
      setTokenProvider();
      setProfileToken();
    };
  }, [isLoaded, isSignedIn, userId, sessionId, getToken, owner, refresh]);
  const selectProfile = async (id: string, pin?: string) => {
    const lifetime = owner.current;
    const cancelled = () => new DOMException('Profile selection was cancelled.', 'AbortError');
    if (!isLoaded || !isSignedIn || !lifetime || activeOwner.current !== owner) throw cancelled();
    const request = ++lifetime.selection;
    const current = () => activeOwner.current === owner &&
      owner.current === lifetime && lifetime.selection === request;
    // A new selection also retires bootstrap work for the previous profile.
    ++lifetime.refresh;
    setLoading(false);
    try {
      const result = await api<{ profileToken: string }>(
        `/profiles/${id}/select`,
        { method: 'POST', body: json({ pin }) },
      );
      if (!current()) throw cancelled();
      setProfileToken(result.profileToken);
      await refresh();
      // An independent refresh may replace bootstrap data without cancelling
      // this selection or its newly acquired authorization.
      if (!current()) throw cancelled();
      setVersion((v) => v + 1);
    } catch (error) {
      if (!current()) throw cancelled();
      throw error;
    }
  };
  return (
    <Context.Provider
      value={{
        signedIn: !!isSignedIn,
        loading: !isLoaded || loading,
        data,
        error,
        authorizationVersion,
        accountScope: owner,
        refresh,
        selectProfile,
      }}
    >
      {children}
    </Context.Provider>
  );
}
